/**
 * RosterTable — a scannable table of records, described as data (BON-538).
 *
 * A CSS grid carrying the ARIA table roles rather than a `<table>`: the roster
 * rows have to stay on one alignment while the surrounding card owns its own
 * scroll container, which a table's own layout algorithm fights.
 *
 * The caller decides which columns exist — dropping one on a narrow screen is a
 * filter over the array, and the track list is derived from the survivors — and
 * supplies `minWidth`, the width below which the table scrolls sideways rather
 * than crushing its own columns.
 */
import type { ReactNode } from 'react';

import { gridTemplateFor, ROSTER_CELL_STYLE, type RosterColumnDef } from './roster-table-types';
import { RosterTableRow } from './RosterTableRow';

export interface RosterTableProps<Row> {
  rows: readonly Row[];
  columns: readonly RosterColumnDef<Row>[];
  /** Stable identity per row. */
  rowKey: (row: Row) => string;
  /** Where a row goes when clicked. Omit for rows that are not links. */
  rowHref?: (row: Row) => string;
  /** Below this the table scrolls sideways instead of compressing. */
  minWidth?: number;
  ariaLabel: string;
  /** Shown in place of the rows when there are none. */
  empty?: ReactNode;
  /** The `rowKey` of the row a sibling surface is currently pointing at. */
  highlightedKey?: string | null;
  /** Fired with a row's key as the pointer enters it, and null as it leaves. */
  onHoverRow?: (key: string | null) => void;
}

export function RosterTable<Row>({
  rows,
  columns,
  rowKey,
  rowHref,
  minWidth = 640,
  ariaLabel,
  empty,
  highlightedKey = null,
  onHoverRow,
}: RosterTableProps<Row>) {
  const template = gridTemplateFor(columns);

  return (
    <div style={{ overflowX: 'auto' }}>
      <div role="table" aria-label={ariaLabel} style={{ minWidth: rows.length ? minWidth : 0 }}>
        <div
          role="row"
          style={{
            display: 'grid',
            gridTemplateColumns: template,
            alignItems: 'center',
            height: 34,
          }}
        >
          {columns.map((column) => (
            <div
              key={column.id}
              role="columnheader"
              className="nb-eyebrow"
              style={{
                ...ROSTER_CELL_STYLE,
                fontSize: 10.5,
                color: 'var(--nb-subtle)',
                justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
              }}
            >
              {column.label}
            </div>
          ))}
        </div>

        {rows.length === 0
          ? empty
          : rows.map((row) => {
              const key = rowKey(row);
              return (
                <RosterTableRow
                  key={key}
                  row={row}
                  columns={columns}
                  template={template}
                  href={rowHref?.(row)}
                  highlighted={key === highlightedKey}
                  onHoverChange={
                    onHoverRow ? (hovered) => onHoverRow(hovered ? key : null) : undefined
                  }
                />
              );
            })}
      </div>
    </div>
  );
}
